// ============= Imports =============
import { Suspense, useEffect, useState } from 'react';
import { useParams, Navigate, Outlet } from 'react-router-dom';
import { LoadingSpinner } from '@tw/tw-component-library';
import { useSignalRHub } from '@/hooks/useSignalRHub';
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';
import { useSignalR } from '@/contexts/signalR';
import {
  addClientToGroupWorkspaceSession,
  removeClientFromGroupWorkspaceSession,
} from '@/apis/core/messaging/workspaceMessageRelayHub';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { UseAuth } from '@/contexts/AuthContext';
import { getWorkspace } from '@/apis/core/workspace/get/getWorkspace';

// ============= Types =============

interface WorkspaceRouteGuardProps {
  /** When used as a layout route, children are omitted and nested content is rendered via Outlet. */
  children?: React.ReactNode;
}

// ============= Component =============

/**
 * Route guard for workspace-specific pages.
 *
 * Responsibilities:
 * - Verifies that the current user is a member of the requested workspace.
 * - Joins the SignalR workspace group (and related Ask group) when authorized.
 * - Updates local online status and re-joins groups after reconnection.
 */
export const WorkspaceRouteGuard: React.FC<WorkspaceRouteGuardProps> = ({
  children,
}) => {
  const { id } = useParams<{ id: string }>();
  const { currentUser, getAccessToken } = UseAuth();
  const {
    getTransformedWorkspaceById,
    getWorkspaceById,
    getWorkspaceNameById,
    getDisplayNameForUser,
    setCurrentWorkspaceId,
    updateUserOnlineStatus,
    isLoading: isWorkspaceContextLoading,
    workspaces,
  } = useWorkspace();
  const workspaceHub = useSignalRHub({ hubName: 'workspaceRelayHub' });
  const { invokeHub } = useSignalR();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Ensure Workspace relay hub handlers are always wired when inside a workspace route.
  // This subscribes to workspace-level events (files, workflows, collections, Ask sessions, etc.)
  // scoped to the current workspaceId.
  useWorkspaceRelayHubHandlers({
    workspaceId: id,
    getDisplayNameForUser,
    getWorkspaceNameById,
    currentUserId: currentUser?.userId,
  });

  // Check if user is authorized to access this workspace
  useEffect(() => {
    // If there's no workspace id in the URL at all, treat as unauthorized.
    if (!id) {
      setIsLoading(false);
      setIsAuthorized(false);
      return;
    }

    // On initial load after a hard refresh, auth can be ready (isAuthenticated)
    // while currentUser is still being populated asynchronously. In that window
    // we should stay in a loading state instead of treating the user as unauthorized,
    // otherwise we incorrectly redirect back to "/".
    if (!currentUser?.userId) {
      return;
    }

    // Wait for workspace context to finish loading before checking authorization
    // This prevents false negatives on page refresh when context is still loading
    if (isWorkspaceContextLoading) {
      console.log(`⏳ Waiting for workspace context to finish loading...`);
      // Keep loading state true while workspace context is loading
      return;
    }

    console.log(
      `🔍 Checking authorization for workspace [${id}]. Context loaded: ${!isWorkspaceContextLoading}, Workspaces in context: ${workspaces.length}`,
    );

    // First, check if workspace is in context (transformed or raw)
    const transformedWorkspace = getTransformedWorkspaceById(id);
    const rawWorkspace = getWorkspaceById(id);
    const workspace = transformedWorkspace || rawWorkspace;

    if (workspace) {
      console.log(`✅ Workspace [${id}] found in context`);
      // Workspace found - check if user is a member
      // Handle both transformed (has .users) and raw (direct object) workspace structures
      const workspaceUsers =
        'workspaceUsers' in workspace && workspace.workspaceUsers
          ? 'users' in workspace.workspaceUsers
            ? workspace.workspaceUsers.users // Transformed structure
            : workspace.workspaceUsers // Raw structure (direct object)
          : workspace.workspaceUsers || {}; // Fallback

      const isMember = Object.keys(workspaceUsers).includes(currentUser.userId);

      console.log(
        `👤 User [${currentUser.userId}] is ${isMember ? 'authorized' : 'NOT authorized'} for workspace [${id}]`,
      );
      setIsAuthorized(isMember);
      setIsLoading(false);
      return;
    }

    // Workspace not in context - try to fetch it to check authorization
    // This handles cases where user navigates directly to a workspace URL
    console.log(`📡 Workspace [${id}] not in context, fetching from API...`);
    const checkAuthorization = async () => {
      if (!getAccessToken) {
        console.error('❌ No access token available');
        setIsLoading(false);
        setIsAuthorized(false);
        return;
      }

      try {
        const fetchedWorkspace = await getWorkspace({
          getAccessToken,
          workspaceId: id,
        });

        console.log(`✅ Successfully fetched workspace [${id}] from API`);

        // Check if current user is a member
        const isMember = Object.keys(
          fetchedWorkspace.workspaceUsers || {},
        ).includes(currentUser.userId);

        console.log(
          `👤 User [${currentUser.userId}] is ${isMember ? 'authorized' : 'NOT authorized'} for workspace [${id}]`,
        );
        setIsAuthorized(isMember);
        setIsLoading(false);
      } catch (error) {
        // Check if error is a 403/404 (access denied) vs network error
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const isAccessDenied =
          errorMessage.includes('403') || errorMessage.includes('404');

        if (isAccessDenied) {
          // User doesn't have access to this workspace
          console.warn(
            `🚫 User [${currentUser.userId}] does not have access to workspace [${id}] (403/404)`,
          );
        } else {
          // Network or other error - log but don't redirect immediately
          // This might be a temporary issue, so we should retry or show an error
          console.error(
            `❌ Failed to fetch workspace [${id}] for authorization check:`,
            error,
          );
        }

        setIsAuthorized(false);
        setIsLoading(false);
      }
    };

    void checkAuthorization();
  }, [
    id,
    currentUser?.userId,
    getTransformedWorkspaceById,
    getWorkspaceById,
    getAccessToken,
    isWorkspaceContextLoading,
    workspaces.length, // Only depend on length to avoid unnecessary re-runs
  ]);

  // Keep context current workspace in sync with route so header/avatars use latest presence
  useEffect(() => {
    if (id && isAuthorized) {
      setCurrentWorkspaceId(id);
      return () => setCurrentWorkspaceId(null);
    }
  }, [id, isAuthorized, setCurrentWorkspaceId]);

  // Join workspace SignalR group when authorized and connected
  // This effect will automatically re-run when connection state changes (including reconnection)
  // due to workspaceHub.isConnected being in the dependency array
  useEffect(() => {
    if (
      !id ||
      !workspaceHub.isConnected ||
      !currentUser?.userId ||
      !isAuthorized
    ) {
      return;
    }

    const userId = currentUser.userId;
    const joinWorkspaceGroup = async () => {
      try {
        await addClientToGroupWorkspaceSession({
          invokeHub,
          workspaceId: id,
          userId,
        });
        console.log(
          `✅ Joined workspace group [${id}] for SignalR notifications`,
        );

        // Mark current user as online locally when they join
        updateUserOnlineStatus(id, userId, true);
        console.log(
          `🟢 Marked current user [${userId}] as online in workspace [${id}]`,
        );
      } catch (error) {
        console.error('Failed to join workspace group:', error);
      }
    };

    // Note: We no longer join the Ask session group here.
    // This is handled by useAskRelayHubHandlers in components that need it (e.g., AskSession).
    // This prevents duplicate joins and ensures the join only happens when actually viewing an Ask session.

    void joinWorkspaceGroup();

    // Cleanup: Tell backend we're leaving (so it can broadcast USER_OFFLINE), then update local state
    return () => {
      if (id && userId) {
        void removeClientFromGroupWorkspaceSession({
          invokeHub,
          workspaceId: id,
          userId,
        });
        updateUserOnlineStatus(id, userId, false);
        console.log(
          `🔴 Marked current user [${userId}] as offline when leaving workspace [${id}]`,
        );
      }
    };
  }, [
    id,
    workspaceHub.isConnected,
    invokeHub,
    currentUser?.userId,
    isAuthorized,
    updateUserOnlineStatus,
  ]);

  // Show loading spinner while checking authorization
  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  // Redirect to home if not authorized
  if (isAuthorized === false) {
    console.warn(
      `🚫 User [${currentUser?.userId}] is not authorized to access workspace [${id}]`,
    );
    return <Navigate to="/" replace />;
  }

  // Render children if provided (legacy), otherwise render nested route content via Outlet.
  // Using a single guard for all /workspace/:id/* keeps the user in the SignalR group and
  // isOnline true while navigating between workspace pages.
  const content = children ?? (
    <Suspense fallback={<LoadingSpinner variant="fullPage" />}>
      <Outlet />
    </Suspense>
  );
  return <>{content}</>;
};
